import { Spacing, Text, Title } from '@vkontakte/vkui';
import classNames from 'clsx';
import { xray } from 'lib/xray';
import React, { type ReactElement, memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IS_ANDROID_MOBILE, IS_DEV, IS_IOS_MOBILE, IS_WEBVIEW } from 'reactApp/appHelpers/configHelpers';
import { tariffBuyCustomDescription } from 'reactApp/appHelpers/featuresHelpers';
import { dmrMidasBuyFrame, dmrMidasBuyFrameWithSidebar } from 'reactApp/appHelpers/featuresHelpers/features/dmrMidas';
import { newProductsApiInBuyWebview } from 'reactApp/appHelpers/featuresHelpers/features/newProductsApi';
import { webviewBuyFrame, webviewBuyFrameEnablePayment } from 'reactApp/appHelpers/featuresHelpers/features/webviewBuyFrame';
import { getQueryParams } from 'reactApp/appHelpers/settingsHelpers';
import { VkBuyModalWithSidebar } from 'reactApp/components/VkBuyModalWithSidebar/VkBuyModalWithSidebar';
import { EPaymentModalType } from 'reactApp/components/VkBuyModalWithSidebar/VkBuyModalWithSidebar.types';
import { checkMobileWebviewPayAvailable } from 'reactApp/modules/billing/billing.helpers';
import { EnvironmentSelectors } from 'reactApp/modules/environment/environment';
import { initProducts } from 'reactApp/modules/products/products.module';
import { ProductsSelectors } from 'reactApp/modules/products/products.selectors';
import { ProductsListActions } from 'reactApp/modules/productsList/productsList.module';
import { ProductsListSelectors } from 'reactApp/modules/productsList/productsList.selectors';
import { showSnackbarAction } from 'reactApp/modules/snackbar/snackbar.actions';
import { SnackbarTypes } from 'reactApp/modules/snackbar/snackbar.types';
import { EPostMessageBuy } from 'reactApp/sections/MobileSubscriptionsPage/MobileSubscriptionsPage.types';
import { PayInBrowser } from 'reactApp/sections/MobileSubscriptionsPage/PayInBrowser/PayInBrowser';
import Content from 'reactApp/ui/Content/Content';
import { useTitle } from 'reactApp/ui/IframePages/Buy/useTitle';
import { TariffBuyConnected } from 'reactApp/ui/TariffBuy/TariffBuy';
import { isFramed } from 'reactApp/utils/isFramed';
import opener from 'reactApp/utils/opener';
import { sendPostMessage } from 'reactApp/utils/windowHelper';

import styles from './Buy.css';
import { useTariff } from './useTariff';

export interface BuyIframeQueryParams {
    productId?: string;
    viewMode?: 'mobile' | 'desktop';
    title?: string;
    description?: string;
    hideSidebar?: 'true';
    hideOtherTariffs?: 'true';
}

const {
    productId: queryProductId,
    viewMode,
    description: descriptionFromQuery,
    hideSidebar: hideSidebarFromQuery,
    hideOtherTariffs: hideOtherTariffsFromQuery,
}: BuyIframeQueryParams = getQueryParams();

enum EPostMessageType {
    size = 'SIZE',
    close = 'CLOSE',
    success = 'SUCCESS',
}

const sendFrameMessage = ({ type, ...data }) => {
    sendPostMessage({ key: 'cloud-fast-checkout', type, ...data });
};

// eslint-disable-next-line max-lines-per-function, sonarjs/cognitive-complexity
export const BuyIframe = memo((): ReactElement | null => {
    const dispatch = useDispatch();
    const titleRef = useRef<HTMLDivElement>(null);

    const showInWebview = IS_WEBVIEW && webviewBuyFrame;

    const newProductApi = showInWebview && newProductsApiInBuyWebview;

    const isMobilePayAvailable = checkMobileWebviewPayAvailable();

    const { product, tariff } = useTariff(queryProductId, showInWebview);

    const isPhone: boolean = (() => {
        switch (viewMode) {
            case showInWebview:
                return true;
            case 'mobile':
                return true;
            case 'desktop':
                return false;
            default:
                return EnvironmentSelectors.isPhone();
        }
    })();

    const hideSidebar = hideSidebarFromQuery === 'true';
    const hideOtherTariffs = hideOtherTariffsFromQuery === 'true';

    const isMidas = hideSidebar ? dmrMidasBuyFrame : dmrMidasBuyFrameWithSidebar;

    const title = useTitle({ isPhone, product, isWebview: showInWebview });

    const { isLoading, isLoaded, hasError } = useSelector(
        newProductApi ? ProductsListSelectors.getLoadingState : ProductsSelectors.getLifeCycleState
    );

    const sendAnalytics = useCallback(
        (action) => {
            const platform = showInWebview ? 'webview' : isPhone ? 'touch' : 'web';

            xray.send(`buy-frame_${action}_${platform}`);
        },
        [showInWebview, isPhone]
    );

    const onDmrResizeEvent = useCallback((height) => {
        // Высота заголовка и spacing'ов вокруг него
        const titleHeight = titleRef.current ? titleRef.current.offsetHeight + 18 + 6 : 0;
        sendFrameMessage({ type: EPostMessageType.size, height: height + titleHeight });
    }, []);

    const onClose = useCallback(() => {
        sendFrameMessage({ type: EPostMessageType.close });
    }, []);

    const onSuccess = useCallback(() => {
        sendFrameMessage({ type: EPostMessageType.success });

        if (showInWebview) {
            const price = product?.hasTrial ? 0 : product?.discountPrice || product?.price || 0;

            dispatch(
                showSnackbarAction({
                    type: SnackbarTypes.success,
                    id: 'buy',
                    text: 'Подписка успешно оформлена',
                    closable: true,
                })
            );

            sendPostMessage({
                paymentResult: EPostMessageBuy.SUCCESS,
                productId: product?.id,
                price,
                family: product?.isFamily,
            });
        }
    }, [product, showInWebview]);

    const onError = useCallback(() => {
        sendAnalytics('error-buy');

        if (showInWebview) {
            sendPostMessage({
                paymentResult: EPostMessageBuy.FAILED,
            });
        }
    }, [showInWebview]);

    useEffect(() => {
        sendAnalytics('view');
        if (newProductApi && queryProductId) {
            dispatch(ProductsListActions.loadProductsList([queryProductId]));
        } else {
            dispatch(initProducts());
        }
    }, []);

    useEffect(() => {
        if (hasError || (isLoaded && !product)) {
            sendAnalytics('error');

            if (showInWebview) {
                sendPostMessage({
                    loadResult: EPostMessageBuy.WINDOW_LOAD_ERROR,
                });
            }
        }

        if (isLoaded && !product) {
            sendAnalytics('no-product');
        }
    }, [hasError, isLoaded, isPhone, product, sendAnalytics]);

    useEffect(() => {
        if (isPhone) {
            return;
        }

        let buyIframeMinWidth = hideSidebar ? 840 : 1200;

        if (isMidas) {
            buyIframeMinWidth = hideSidebar ? 520 : 830;
        }

        sendFrameMessage({ type: EPostMessageType.size, width: buyIframeMinWidth });
    }, [isPhone, hideSidebar]);

    if (!(isFramed() || showInWebview) && !IS_DEV) {
        xray.send(`buy-frame_error_not_frame`);

        if (!showInWebview) {
            opener('/', true);
        }
    }

    const content = useMemo(() => {
        const isCustomDescription = descriptionFromQuery && tariffBuyCustomDescription?.hasOwnProperty(descriptionFromQuery);
        const customDescription = isCustomDescription ? tariffBuyCustomDescription[descriptionFromQuery] : undefined;

        if (!isLoaded) {
            return;
        }

        if (!isMobilePayAvailable && !webviewBuyFrameEnablePayment) {
            return (
                <PayInBrowser
                    id={product?.id || ''}
                    isAndroidPromo={IS_ANDROID_MOBILE}
                    isIOSPromo={IS_IOS_MOBILE}
                    onClose={() => sendAnalytics('browser-close')}
                    onCopy={() => sendAnalytics('browser-copy')}
                    onOpen={() => sendAnalytics('browser-open')}
                    closable={false}
                />
            );
        }

        if (isPhone) {
            if (isLoaded && !product) {
                return (
                    <div className={styles.noTariff}>
                        <Spacing size={20} />
                        <Title size={2}>Этот тариф для вас недоступен</Title>
                        <Spacing size={10} />
                        <Text>
                            Вероятно, сейчас или в прошлом у вас уже была подписка Mail Space, а акция действует только для новых
                            пользователей. Смените аккаунт и попробуйте еще раз
                        </Text>
                    </div>
                );
            }

            return (
                <TariffBuyConnected
                    id={product?.id}
                    onClose={onClose}
                    onSuccess={onSuccess}
                    onError={onError}
                    isMobile
                    closable={false}
                    source="buy-frame"
                    title={title}
                    paymentDescription={customDescription}
                    eventCategory={showInWebview ? 'webviewBuyFrame' : 'touchBuyFrame'}
                />
            );
        }

        if (!product) {
            return;
        }

        return (
            <>
                <Spacing size={18} />
                <div className={styles.title} ref={titleRef}>
                    {title}
                </div>
                <Spacing size={6} />

                <VkBuyModalWithSidebar
                    productId={product?.id}
                    tariff={tariff}
                    isFrame
                    onClose={onClose}
                    onSuccess={onSuccess}
                    type={EPaymentModalType.fastcheckout}
                    onDmrResize={onDmrResizeEvent}
                    withoutSidebar={hideSidebar}
                    paymentDescription={customDescription}
                    hideOtherTariffs={hideOtherTariffs}
                    isMidasFrameEnabled={isMidas}
                />
            </>
        );
    }, [isPhone, onClose, onDmrResizeEvent, onSuccess, product, tariff, title, hideSidebar, isMidas, isLoaded]);

    return (
        <div
            className={classNames(styles.root, {
                [styles.root_midas]: isMidas,
                [styles.root_noSidebar]: isMidas && hideSidebar,
            })}
        >
            <Content
                prerenderContent={false}
                wrapClass={styles.content}
                isLoading={isLoading}
                hasError={hasError || (!isPhone && isLoaded && !product)}
                qaId="buy"
            >
                {content}
            </Content>
        </div>
    );
});

BuyIframe.displayName = 'BuyIframe';
